import { Typography } from "antd";
import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type RefObject,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import type { ChartType } from "@/warRoom/graph/spec";
import { useGraphStore } from "@/warRoom/store/useGraphStore";

import WidgetCard from "./WidgetCard";
import { useGuideStore } from "./guides";
import { CARD_DEFAULT_W, CHART_DND_MIME, contentBounds } from "./layout";

const { Text } = Typography;

/** 畫布內容之外多留的空間，讓最後一張圖右邊／下面還拖得動 */
const SLACK = 160;

const chartTypeFrom = (e: DragEvent) =>
  (e.dataTransfer.getData(CHART_DND_MIME) ||
    e.dataTransfer.getData("text/plain")) as ChartType | "";

/**
 * 中間的畫布：一塊可以放很多張圖的平面。
 *
 * 位置是絕對座標（吸附 8px 格），不是自動排版的 grid —— 使用者要能把兩張圖並排、
 * 也能把一張放大擺在上面。捲軸在外層，卡片在 surface 上；
 * 匯出 PNG 用的就是 surface（所以捲動位置不影響輸出）。
 */
export default function GraphCanvas({
  surfaceRef,
}: {
  surfaceRef: RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const widgets = useGraphStore((s) => s.widgets);
  const selectedId = useGraphStore((s) => s.selectedId);
  const act = useGraphStore.getState;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [dropping, setDropping] = useState(false);

  // 量畫布寬度：「點一下放進畫布」要靠它決定一列排幾張
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () =>
      useGraphStore.getState().setCanvasWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 選中的圖按 Delete／Backspace 移除。
  // 在輸入框裡打字、或焦點在工具箱裡（正在調軸、拉前 N 名）時不能攔。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const t = e.target as HTMLElement | null;
      const typing =
        !!t &&
        (t.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
      if (typing || t?.closest?.(".toolbox")) return;
      const id = useGraphStore.getState().selectedId;
      if (!id) return;
      e.preventDefault();
      useGraphStore.getState().removeWidget(id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const bounds = contentBounds(widgets);

  const onDrop = (e: DragEvent) => {
    const type = chartTypeFrom(e);
    setDropping(false);
    if (!type) return;
    e.preventDefault();
    const surface = surfaceRef.current;
    if (!surface) return;
    const rect = surface.getBoundingClientRect();
    // 放在游標處：卡片的標題列對著游標中央，跟拖曳時的殘影對得起來
    act().addWidget(type, {
      x: e.clientX - rect.left - CARD_DEFAULT_W / 2,
      y: e.clientY - rect.top - 16,
      canvasWidth: rect.width,
    });
  };

  return (
    <div
      className={`canvas${dropping ? " is-dropping" : ""}`}
      ref={scrollRef}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(CHART_DND_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setDropping(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDropping(false);
      }}
      onDrop={onDrop}
      // 點空白處＝取消選取（點在卡片上時卡片自己會 stopPropagation 之外的處理）
      onPointerDown={(e) => {
        if (!(e.target as HTMLElement).closest("[data-widget]"))
          act().selectWidget(null);
      }}
    >
      <div
        ref={surfaceRef}
        className="canvas__surface"
        style={{
          minWidth: bounds.right + SLACK,
          minHeight: Math.max(bounds.bottom + SLACK, 480),
        }}
      >
        {widgets.length === 0 ? (
          <div className="canvas__empty" data-export="skip">
            <Text strong>{t("warRoom.canvas.emptyTitle")}</Text>
            <Text type="secondary">
              <Trans
                i18nKey="warRoom.canvas.emptyHint"
                components={{ strong: <Text strong /> }}
              />
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("warRoom.canvas.emptyMulti")}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("warRoom.canvas.emptyMixed")}
            </Text>
          </div>
        ) : null}

        {widgets.map((w) => (
          <WidgetCard key={w.id} widget={w} selected={w.id === selectedId} />
        ))}

        <AlignGuides />
      </div>
    </div>
  );
}

function AlignGuides() {
  const guides = useGuideStore((s) => s.guides);
  if (guides.length === 0) return null;
  return (
    <>
      {guides.map((g) => (
        <div
          key={`${g.axis}${g.pos}`}
          className={`canvas__guide canvas__guide--${g.axis}`}
          data-export="skip"
          style={
            g.axis === "v"
              ? { left: g.pos, top: g.from, height: g.to - g.from }
              : { top: g.pos, left: g.from, width: g.to - g.from }
          }
        />
      ))}
    </>
  );
}
